import { titleJoiner } from "@/lib/title-joiner";
import { getLocale } from "@/paraglide/runtime";

type EmphasizedTitleProps = {
  lead: string;
  accent: string;
  /** Put the accent on the next line (hero / section titles). */
  stacked?: boolean;
  /** Accent fills its own block line (home brand "Golf"). */
  accentBlock?: boolean;
  /** Defaults to the active paraglide locale. */
  locale?: string;
};

function activeLocale(): string {
  try {
    return getLocale();
  } catch {
    return "en";
  }
}

/**
 * White lead + ember accent, matching the pre-i18n marketing title style.
 * An empty `accent` renders `lead` alone.
 */
export function EmphasizedTitle({
  lead,
  accent,
  stacked,
  accentBlock,
  locale,
}: EmphasizedTitleProps) {
  // Locales without inter-word spaces (zh/ja/th) may carry the whole title in
  // `lead` with an empty `accent`; render it verbatim with no stray space.
  if (!accent) return <>{lead}</>;

  const joiner = titleJoiner(locale ?? activeLocale(), lead, accent);
  const accentEl = (
    <span className={accentBlock ? "block text-ember" : "text-ember"}>{accent}</span>
  );

  if (stacked) {
    return (
      <>
        {lead}
        {joiner}
        <br />
        {accentEl}
      </>
    );
  }

  // accentBlock: the accent is display:block, so the joiner only matters for copy/paste
  // and screen readers; inline: it is the visible gap.
  return (
    <>
      {lead}
      {joiner}
      {accentEl}
    </>
  );
}
